import { useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';

import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { UserContext } from 'Context/user';

import { useFeatureFlags } from 'Utilities/hooks';
import { useNetworkStatus } from 'Utilities/user/hooks/useNetworkStatus';
import { useSetRefreshToken } from 'Utilities/user/hooks/useSetRefreshToken';
import { useVisibilityStatus } from 'Utilities/user/hooks/useVisibilityStatus';

import texts from '../../../../texts.json';
import { DOCUMENT_CAPTURE_ROUTE_NAMES } from '../../documentCapture/DocumentCapture.helpers';

const LocalT = new MDText(texts);

export const useCognito = () => {
  const { userActions: { update: updateUser }, userState: user } = useContext(UserContext);
  const isRefreshingToken = useRef<boolean>(false);

  const history = useHistory();
  const { pathname } = useLocation();

  const { useAuthnIdToken } = useFeatureFlags();

  const isPostSaleDocsFlow = pathname.includes(DOCUMENT_CAPTURE_ROUTE_NAMES.AUTOMATED_DOCS_PAGE);

  const { setCognitoIdTokenRefreshInterval, stopRefreshInterval, triggerRefreshInterval } = useSetRefreshToken();
  const { cleanupNetworkListeners, setupNetworkListeners } = useNetworkStatus({
    handleOffline: stopRefreshInterval,
    handleOnline: triggerRefreshInterval,
  });
  const { cleanupVisibilityListeners, setupVisibilityListeners } = useVisibilityStatus({
    handleOffVisibility: stopRefreshInterval,
    handleOnVisibility: triggerRefreshInterval,
  });

  const triggerRefreshTokenRequestonExpiry = useCallback(() => {
    const now = Date.now();

    const timeSinceLastRefresh = now - (user?.lastTokenRefresh || 0);
    const shouldSkipDueToTiming = timeSinceLastRefresh < ((user?.cognitoTokens?.expiresIn ?? 0) * 1000);

    if (shouldSkipDueToTiming || isRefreshingToken.current) {
      updateUser({ refreshSession: false });

      window?.Sentry?.captureMessage?.('Token refresh skipped', {
        extra: {
          isRefreshingToken: isRefreshingToken.current,
          reason: shouldSkipDueToTiming ? 'within_24h_window' : 'already_refreshing',
          timeSinceLastRefresh,
          timestamp: now,
        },
        level: 'info',
      });
      return;
    }

    isRefreshingToken.current = true;

    updateUser({
      lastTokenRefresh: now,
      refreshSession: false,
    });

    setCognitoIdTokenRefreshInterval(true);

    setTimeout(() => {
      isRefreshingToken.current = false;
    }, 5000);
  }, [user?.lastTokenRefresh, user?.cognitoTokens?.expiresIn, updateUser, setCognitoIdTokenRefreshInterval]);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(history.location.search));

    if (params?.logout === 'notify') {
      toast({
        content: LocalT.translate('components.userSession.error.content'),
        heading: LocalT.translate('components.userSession.error.title'),
        id: 'logout-notification',
        variant: 'error',
      });
      history.replace({
        search: '',
      });
    }
  }, [history]);

  useEffect(() => {
    if (useAuthnIdToken && Number.isInteger(user?.id) && user?.cognitoTokens?.refreshToken) {
      setCognitoIdTokenRefreshInterval(false);
    }

    if (useAuthnIdToken && Number.isInteger(user?.id) && user?.cognitoTokens?.refreshToken && user?.refreshSession) {
      triggerRefreshTokenRequestonExpiry();
    }
  }, [
    useAuthnIdToken,
    user?.id,
    user?.cognitoTokens?.refreshToken,
    user?.refreshSession,
    setCognitoIdTokenRefreshInterval,
    triggerRefreshTokenRequestonExpiry,
  ]);

  useEffect(() => {
    if (useAuthnIdToken && Number.isInteger(user?.id) && user?.cognitoTokens?.refreshToken && !isPostSaleDocsFlow) {
      setupNetworkListeners();
      setupVisibilityListeners();
    }
    return () => {
      cleanupNetworkListeners();
      cleanupVisibilityListeners();
    };
  }, []);
};
